// services/audioDiagnostics.js
//
// A small always-on recorder for what the audio system is actually doing.
//
// WHY THIS EXISTS. Audio failures on a TV are silent. Howler reports a failed
// decode as a `loaderror` event on the Howl, and if nobody is listening the
// clip simply never plays — no exception, no console entry, nothing on screen.
// On a low-end device that is exactly the failure that matters: the browser
// runs out of room for decoded PCM, the long clips (music beds, `downInto`,
// `lockIn`) fail to decode, and the game plays on in near-silence looking
// perfectly healthy. A developer console is not available on the TV, so the
// evidence has to be renderable.
//
// Everything here is recorded unconditionally into a small ring buffer, whether
// or not the overlay is open — so when someone turns it on mid-game they see
// the history that led here, not an empty list. Recording is a push onto a
// capped array; there is no formatting or serialising cost per event.
//
// Read it with `AudioDebugOverlay` (on the host by default), or from a console
// with `window.__audioDiag.summary()`.

/** How many events to keep. Oldest fall off the front. */
const MAX_EVENTS = 80;

/** Event severities, in the order the overlay colours them. */
export const LEVEL = {
    ERROR: "error",
    WARN: "warn",
    INFO: "info",
};

/** Strip a webpack-hashed asset URL down to something readable on a TV. */
export function clipName(src) {
    if (!src) return "(none)";
    const url = Array.isArray(src) ? src[0] : String(src);
    const file = url.split("/").pop() ?? url;
    // "sfx_downInto_2000.a5a42e7c.mp3" -> "sfx_downInto_2000"
    return file.replace(/\.[0-9a-f]{8,}(?=\.)/, "").replace(/\.(mp3|ogg|wav|m4a)$/i, "");
}

class AudioDiagnostics {
    constructor() {
        /** @type {{id:number, t:number, level:string, type:string, clip:string, message:string}[]} */
        this.events = [];
        /** @type {Set<(snapshot:object)=>void>} */
        this.listeners = new Set();
        this.counts = { loaded: 0, loadError: 0, playError: 0, played: 0, evicted: 0, primed: 0 };
        this._nextId = 1;
        this._startedAt = Date.now();
        // Coalesce notifications: a boot preload fires dozens of events in one
        // tick and the overlay only needs to repaint once at the end of it.
        this._notifyScheduled = false;
    }

    /**
     * Record one event.
     * @param {string} level - LEVEL.*
     * @param {string} type - short tag, e.g. "loaderror", "play", "evict"
     * @param {string|string[]} src - clip source (hashed url is fine, it's shortened)
     * @param {string} [message] - detail
     */
    record(level, type, src, message = "") {
        const event = {
            id: this._nextId++,
            t: Date.now() - this._startedAt,
            level,
            type,
            clip: clipName(src),
            message,
        };
        this.events.push(event);
        if (this.events.length > MAX_EVENTS) this.events.splice(0, this.events.length - MAX_EVENTS);

        // Errors also go to the console — harmless on a TV, useful when a dev
        // machine IS attached, and it keeps the existing console-first habit.
        if (level === LEVEL.ERROR) console.error(`[audio] ${type} ${event.clip}`, message);

        this._scheduleNotify();
    }

    /** Bump a counter without logging a line (for high-frequency successes). */
    count(key, delta = 1) {
        if (this.counts[key] == null) this.counts[key] = 0;
        this.counts[key] += delta;
        this._scheduleNotify();
    }

    _scheduleNotify() {
        if (this._notifyScheduled || this.listeners.size === 0) return;
        this._notifyScheduled = true;
        const flush = () => {
            this._notifyScheduled = false;
            this.listeners.forEach(fn => { try { fn(this); } catch { } });
        };
        // rAF keeps the repaint on a frame boundary; setTimeout is the fallback
        // for a backgrounded tab, where rAF never fires.
        if (typeof requestAnimationFrame === "function") requestAnimationFrame(flush);
        else setTimeout(flush, 0);
    }

    /**
     * Subscribe to changes.
     * @param {(diag:AudioDiagnostics)=>void} fn
     * @returns {() => void} unsubscribe
     */
    subscribe(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    clear() {
        this.events.length = 0;
        Object.keys(this.counts).forEach(k => { this.counts[k] = 0; });
        this._scheduleNotify();
    }

    /** Errors only, newest first — what the overlay leads with. */
    get errors() {
        return this.events.filter(e => e.level === LEVEL.ERROR);
    }

    /** Console-friendly one-shot dump: `window.__audioDiag.summary()`. */
    summary() {
        return {
            counts: { ...this.counts },
            errors: this.errors.map(e => `${e.t}ms ${e.type} ${e.clip}: ${e.message}`),
        };
    }
}

/** Singleton — imported by AudioManager (writer) and the overlay (reader). */
export const audioDiag = new AudioDiagnostics();

// Reachable from a remote debugger / dev console without an import. Named
// generically, not per-game: these three audio files (AudioManager,
// audioDiagnostics, AudioDebugOverlay) are meant to drop into any GameCity
// game unchanged, so nothing in them should carry one game's name.
if (typeof window !== "undefined") window.__audioDiag = audioDiag;
